import * as React from 'react';
import { EDropdownOptionsButtonSource, ICustomPropertiesFormProps } from './types';
import { CLASS_CUSTOM_PROPERTY, CLASS_CUSTOM_PROPERTY_ITEM } from '../constants';
import { Button, Icon, Input } from 'kui';
import './_CustomPropertiesForm.scss';
import { ECustomPropertyType, IBoardCardCustomOption } from '../../../../../../../store/customProperties/types';
import {
    CARD_CUSTOM_FORM_OPTION_PLACEHOLDER,
    CARD_CUSTOM_FORM_OPTION_PLACEHOLDER_FOCUS,
    CARD_CUSTOM_FORM_PLACEHOLDER_FOCUS,
    CARD_CUSTOM_SELECT_DEFAULT_OPTIONS_COUNT,
    CUSTOM_BUTTON_REMOVE_TOOLTIP,
    CUSTOM_ERROR_DUPLICATE,
    CUSTOM_ERROR_EMPTY,
    getPropertyPlaceholder
} from './constants';
import { ErrorHint } from '../../../ErrorHint/ErrorHint';
import { v4 as uuidv4 } from 'uuid';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { CLASS_LIST_ITEM_CONTAINER } from '../../../ListItem/constants';
import { ListItem } from '../../../ListItem/ListItem';
import { TCustomOptionId } from '../../../../../../../types/types';
import { CUSTOM_PROPERTY_TYPES } from '../../../../../../../store/customProperties/constants';
import { AnalyzeRender } from '../../../../../helpers/memoizeHelper';
import { CustomPropertyTypeSelect } from '../CustomPropertyTypeSelect/CustomPropertyTypeSelect';
import { getParentsClasses } from '../../../../../helpers/getParentsClasses';
import { getAsidePanelTooltip } from '../../../../../aside_panel/asidePanel/helpers/getAsidePanelTooltip';
import { AsidePanelContext } from '../../../../../aside_panel/asidePanel/components/AsidePanel/constants';
import { ICustomPropertiesElement } from '../CustomProperty/types';
import { trimLow } from '../../helpers/getCustomPropertyValid';
// @ts-ignore
const analyzeRender = window.Settings.development ? new AnalyzeRender(`CustomPropertiesForm`) : null;

export function CustomPropertiesForm ({
    element,
    isCloseOnAdd,
    isNew,
    isReadonly,
    isPopup,
    names,
    onChange,
    onChangeType,
    onClose,
    onDropdownOptionsButton,
    onEnter,
    onOptionAdd,
    onSortOptions,
    setNewProperty,
}: ICustomPropertiesFormProps) {
    const { cardId } = React.useContext(AsidePanelContext);

    if (analyzeRender && element) analyzeRender.call(element.id);
    const className = CLASS_CUSTOM_PROPERTY + '__form';
    const [uniqueClass] = React.useState(className + '--' + uuidv4());
    const classInputContainer = className + '-input';
    const classLabel = className + '-label';

    const classSelect = CLASS_CUSTOM_PROPERTY_ITEM + '-select';
    const classOptions = CLASS_CUSTOM_PROPERTY_ITEM + '-options';
    const classOption = CLASS_CUSTOM_PROPERTY_ITEM + '-option';
    const classOptionAdd = CLASS_CUSTOM_PROPERTY_ITEM + '-option-add';
    const classOptionInput = CLASS_CUSTOM_PROPERTY_ITEM + '-option-input';

    let [elementState, setElement] = React.useState<ICustomPropertiesElement>({
        ...element,
        id: element ? element.id : uuidv4(),
        name: element ? element.name : '',
        type: element ? element.type : Object.keys(CUSTOM_PROPERTY_TYPES)[0] as ECustomPropertyType,
    });
    const [type, setType] = React.useState(element && element.type || ECustomPropertyType.TEXT);
    const [isOpened, setOpened] = React.useState(isNew);
    const [isFocus, setFocus] = React.useState(null);
    const [focusedItem, setFocusedItem] = React.useState(null);

    const inputRef = React.useRef(null);

    const namesTrim = names ? names.map(name => trimLow(name)) : [];

    const isValid = (): boolean => {
        const nameTrim = trimLow(elementState.name);
        const isEmpty = nameTrim === '';
        const duplicates = namesTrim.filter(name => name === nameTrim);
        setElement({
            ...elementState,
            error: isEmpty
                ? CUSTOM_ERROR_EMPTY
                : duplicates.length ? CUSTOM_ERROR_DUPLICATE : null,
        });
        return !isEmpty && !duplicates.length;
    }

    const getDefaultNewOptions = (): IBoardCardCustomOption[] => {
        const options: IBoardCardCustomOption[] = [];
        for (let i = 1; i <= CARD_CUSTOM_SELECT_DEFAULT_OPTIONS_COUNT; i++) {
            options.push({
                id: uuidv4(),
            })
        }
        return options;
    }

    const onChangeTypeHandler = (type: ECustomPropertyType) => {
        const newElement: ICustomPropertiesElement = {
            ...elementState,
            type
        };
        if (type === ECustomPropertyType.SELECT) {  // для select добавляем 3 дефолтных опции
            if (!newElement.options || !newElement.options.length) {
                newElement.options = getDefaultNewOptions();
            }
        } else {
            newElement.options = [];
        }
        setElement(newElement);
        setType(type);
        if (element) {
            onChange(elementState.name, type, newElement.options);
        }
        onChangeType(type);
        const input = inputRef.current && inputRef.current.getInput();
        if (input) input.focus();
    };

    const onChangeName = (
        event: React.FormEvent
    ) => {
        const { value } = event.target as HTMLInputElement;
        elementState.name = value;
        setElement({...elementState});
        if (element) {
            onChange(value, elementState.type, element.options);
        }
        if (elementState.error) isValid();
    }

    const onClickAdd = () => {
        if (
            elementState.name &&
            elementState.name.trim()
        ) {
            if (!isValid() || !showAddBtn) return;

            const options = elementState.options && elementState.options.filter(option => !!option.value);
            setElement({
                ...elementState,
                name: '',
                options: elementState.type === ECustomPropertyType.SELECT ? getDefaultNewOptions() : null
            });
            if (isCloseOnAdd && onClose) {
                onClose();
            } else {
                onChange(elementState.name, elementState.type, options);
            }
            setTimeout(() => {
                const container = document.querySelector(`.${CLASS_CUSTOM_PROPERTY}__items:not(.${CLASS_CUSTOM_PROPERTY}__items--card)`) as HTMLElement;
                if (container) {
                    const item = container.querySelector(`.${CLASS_CUSTOM_PROPERTY_ITEM} .kui-input__item`) as HTMLElement;
                    if (item && item.focus) item.focus(); // focus if input
                    if (item && item.click) item.click(); // open if select
                }
            }, 300);
        }
    };

    const onKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement>
    ) => {
        if (e) {
            if (e.key === 'Enter' &&
                !e.shiftKey
            ) {
                e.preventDefault();
                if (!element) {
                    onClickAdd();
                }
                if (onEnter) onEnter();
            }
            if (e.key === 'Escape') {
                if(onClose)
                    onClose();
                e.stopPropagation();//to avoid aside-panel closure on esc if input is in focus
            }
        }
    };

    const isOneOptionFilled = (): boolean => {
        if (elementState.options && elementState.options.length) {
            return !!elementState.options.find(option => !!option.value);
        }
        return true;
    }

    const showAddBtn = !!elementState.name && elementState.name.trim() && isOneOptionFilled();

    function onDragEnd(result: DropResult) {
        if (
            !result.destination ||
            result.destination.index === result.source.index
        ) {
            // do nothing
        } else {
            onSortOptions(result.destination.droppableId, result.source.index, result.destination.index);
        }
    }

    const storeOptions = (
        options: IBoardCardCustomOption[]
    ) => {
        setElement({
            ...elementState,
            options
        })
        if (element) {
            onChange(elementState.name, elementState.type, options);
        }
    }

    const onOptionAddHandler = () => {
        const id = uuidv4();
        const options = [
            ...elementState.options,
            { id }
        ]
        storeOptions(options);
        if (element) {
            onOptionAdd(elementState.id, elementState);
        }
        onDropdownOptionsButton(EDropdownOptionsButtonSource.ADD);
        setTimeout(() => {
            const item = document.querySelector(`.${CLASS_LIST_ITEM_CONTAINER}--${id} .kui-input__item`) as HTMLElement;
            if (item) item.focus();
        }, 0);
    }

    const onOptionDeleteHandler = (optionId: TCustomOptionId) => {
        const options = elementState.options.filter(option => option.id !== optionId);
        storeOptions(options);
        onDropdownOptionsButton(EDropdownOptionsButtonSource.REMOVE)
    }

    const onChangeOptionHandler = (event: React.FormEvent, optionId: TCustomOptionId) => {
        const target = event.target as HTMLInputElement;
        let { value = null } = target;
        const options = elementState.options.map((option, index) => {
            if (option.id === optionId) {
                return {
                    ...option,
                    value
                }
            }
            return option;
        })
        storeOptions(options);
    }

    React.useEffect(() => {
        if (element) {
            setElement({
                ...elementState,
                error: element.error,
                options: element.options
            });
        }
    }, [element]);

    React.useEffect(() => {
        if (elementState.name) {
            setOpened(false);
        }
    }, [elementState]);

    React.useEffect(() => {
        if (isNew) {
            setTimeout(() => {
                const input = inputRef.current && inputRef.current.getInput();
                if (input) input.focus();
            }, 0);
        }
    }, [isNew]);

    React.useEffect(() => {
        if (!setNewProperty) return;
        const newElement: ICustomPropertiesElement = elementState.name ? {
            ...elementState,
            type,
        } : null;
        setNewProperty(newElement);
    }, [elementState]);

    return (
        <div className={`
            ${className}
            ${element ? className + '--' + element.id : ''}
            ${uniqueClass}
        `}>
            <div className={className + '-row'}>
                <div className={classInputContainer}>
                    <Input
                        ref={inputRef}
                        className={classLabel}
                        autosize={false}
                        placeholder={isFocus ? CARD_CUSTOM_FORM_PLACEHOLDER_FOCUS : getPropertyPlaceholder(type)}
                        readOnly={isReadonly}
                        state={elementState.error || focusedItem && !elementState.name ? 'error' : null}
                        value={elementState.name}
                        onChange={onChangeName}
                        onKeyDown={onKeyDown}
                        onFocus={() => setFocus(true)}
                        autoComplete={'off'}
                        onBlur={() => {
                            setOpened(false);
                            setFocus(false);
                        }}
                    />
                    <CustomPropertyTypeSelect cardId={cardId} type={type} onChange={isNew || isPopup ? onChangeTypeHandler : null} isOpened={isOpened} />
                </div>
                {elementState.error &&
                    <ErrorHint
                        arrow={'down'}
                        direction={'up'}
                        portalSelector={`.${uniqueClass}`}
                        selector={`.${uniqueClass}`}
                        value={elementState.error}
                    />
                }
            </div>
            {elementState.type === ECustomPropertyType.SELECT &&
                <div className={classSelect}>
                    {!!elementState.options && !!elementState.options.length &&
                        <DragDropContext
                            onDragEnd={onDragEnd}
                        >
                            <Droppable
                                droppableId={elementState.id}
                            >
                                {(provided, snapshot) => (
                                    <div
                                        className={classOptions}
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                    >
                                        {elementState.options.map((option, index) => {
                                            const count = index + 1;
                                            const actions = isReadonly || elementState.options.length <= 1 ? null
                                                : <Button
                                                    variant={'icon'}
                                                    tooltip={getAsidePanelTooltip({
                                                        direction: 'up-left',
                                                        value: CUSTOM_BUTTON_REMOVE_TOOLTIP,
                                                    })
                                                    }
                                                    onClick={() => onOptionDeleteHandler(option.id)}
                                                >
                                                    <Icon size={24} xlink={'delete'} />
                                                </Button>;

                                            return (
                                                <Draggable
                                                    draggableId={option.id}
                                                    index={index}
                                                    isDragDisabled={isReadonly || isPopup || !element || elementState.options.length === 1}
                                                    key={option.id}
                                                >
                                                    {(provided, snapshot) => {
                                                        return (
                                                            <div
                                                                className={`
                                                                    ${CLASS_LIST_ITEM_CONTAINER}
                                                                    ${CLASS_LIST_ITEM_CONTAINER}--option
                                                                    ${CLASS_LIST_ITEM_CONTAINER}--${option.id}`
                                                                }
                                                                key={option.id}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                ref={provided.innerRef}
                                                            >
                                                                <ListItem
                                                                    actions={actions}
                                                                    className={classOption}
                                                                    isDraggable={!isReadonly && !isPopup && !!element && elementState.options.length > 1}
                                                                    text={<Input
                                                                        className={classOptionInput}
                                                                        onFocus={() => setFocusedItem(count)}
                                                                        onBlur={e => {
                                                                            const className = CLASS_LIST_ITEM_CONTAINER + '--option';
                                                                            const parentClasses = getParentsClasses(e.relatedTarget as HTMLElement, [className]);
                                                                            if (parentClasses.includes(className)) return;
                                                                            setFocusedItem(null);
                                                                        }}
                                                                        autosize={false}
                                                                        placeholder={focusedItem === count ? CARD_CUSTOM_FORM_OPTION_PLACEHOLDER_FOCUS : `${CARD_CUSTOM_FORM_OPTION_PLACEHOLDER} ${count}`}
                                                                        readOnly={isReadonly}
                                                                        value={option.value}
                                                                        onKeyDown={e => {
                                                                            if (e.key === 'Enter' && option.value) {
                                                                                onOptionAddHandler();
                                                                            }
                                                                        }}
                                                                        onChange={(event) => onChangeOptionHandler(event, option.id)}
                                                                    />}
                                                                />
                                                            </div>
                                                        );
                                                    }}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    }
                    <Button
                        className={classOptionAdd}
                        variant={'add'}
                        onClick={onOptionAddHandler}
                    >
                        Add option
                    </Button>
                </div>
            }
        </div>
    );
};
